import { Button, Typography } from '@mui/material';
import { ALL_ACCESS_PROFILE_ROLE, ProfileRoleType } from '@strength/domain';
import { MdAdd } from '@teamworksdev/icons';
import { v4 as uuid } from 'uuid';
import { Sheet, Slot } from '../../../../components/sheet';
import { CustomSlot } from '../../../../components/sheet/slots';
import { ProfileRoleSlot } from './ProfileRoleSlot';
import { CoachProfile, ProfileRoleAssignment } from './types';

export type { CoachProfile, ProfileRoleAssignment } from './types';

export type ProfileRoleSheetProps = {
  /** Feature flag for this org (from server; LaunchDarkly org context). */
  bowlingBlockTypeFlag: boolean;
  profileRoles: ProfileRoleAssignment[];
  coaches: CoachProfile[];
  onChange: (profileRoles: ProfileRoleAssignment[]) => void;
};

export function ProfileRoleSheet({
  bowlingBlockTypeFlag,
  profileRoles,
  coaches,
  onChange,
}: ProfileRoleSheetProps) {
  const availableCoaches = coaches.filter(
    (c) => !profileRoles.some((pr) => pr.profileId === c.id),
  );

  const handleAddRole = (profileId: string, role: ProfileRoleType) => {
    onChange([...profileRoles, { id: uuid(), profileId, role }]);
  };

  const handleUpdateRole = (profileRoleId: string, role: ProfileRoleType) => {
    onChange(
      profileRoles.map((pr) =>
        pr.id === profileRoleId ? { ...pr, role } : pr,
      ),
    );
  };

  const handleDeleteRole = (profileRoleId: string) => {
    onChange(profileRoles.filter((pr) => pr.id !== profileRoleId));
  };

  const handleChangeCoach = (
    profileRoleId: string,
    newProfileId: string,
    _role: ProfileRoleType,
  ) => {
    onChange(
      profileRoles.map((pr) =>
        pr.id === profileRoleId ? { ...pr, profileId: newProfileId } : pr,
      ),
    );
  };

  return (
    <Sheet
      {...getSlotsForProfileRoles(
        profileRoles,
        coaches,
        handleUpdateRole,
        handleDeleteRole,
        handleChangeCoach,
        handleAddRole,
        availableCoaches,
        bowlingBlockTypeFlag,
      )}
      onUndo={() => {}}
      onRedo={() => {}}
      onBulkClear={() => {}}
      getContextMenuContent={() => null}
      onCopy={() => {}}
      onPaste={() => {}}
      onDragEnd={() => {}}
    />
  );
}

type ProfileRoleSlotType = Slot<ProfileRoleAssignment>;

function getSlotsForProfileRoles(
  profileRoles: ProfileRoleAssignment[],
  coaches: CoachProfile[],
  onUpdateRole: (profileRoleId: string, role: ProfileRoleType) => void,
  onDeleteRole: (profileRoleId: string) => void,
  onChangeCoach: (
    profileRoleId: string,
    newProfileId: string,
    role: ProfileRoleType,
  ) => void,
  onAddRole: (profileId: string, role: ProfileRoleType) => void,
  availableCoaches: CoachProfile[],
  bowlingBlockTypeFlag: boolean,
) {
  const slots: ProfileRoleSlotType[] = [];
  const columnCount = 2;
  const rowCount = profileRoles.length + 2;
  const gridSubRows = 1;

  // Main slot for header and data rows
  slots.push(
    new ProfileRoleSlot(
      { x: 0, y: 0 },
      profileRoles,
      coaches,
      onUpdateRole,
      onDeleteRole,
      onChangeCoach,
      false,
      gridSubRows,
      bowlingBlockTypeFlag,
    ),
  );

  // Add button row
  slots.push(
    new CustomSlot({ x: 0, y: rowCount - 1 }, columnCount, 1, false, () => (
      <Button
        variant="text"
        sx={{ height: '100%', width: '100%' }}
        onClick={() => {
          const firstCoach = availableCoaches[0];
          if (firstCoach) {
            onAddRole(firstCoach.id, ALL_ACCESS_PROFILE_ROLE);
          }
        }}
        disabled={availableCoaches.length === 0}
        className="bg-gray-50"
      >
        <MdAdd className="text-black/60" />
        <Typography variant="subtitle1" className="text-black/60">
          Add Role
        </Typography>
      </Button>
    )),
  );

  return {
    slots,
    gridSubRows,
    columnCount,
    rowCount,
  };
}
